/* eslint-disable react-hooks/exhaustive-deps */
import { Copy, Search, RotateCcw } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Table,
  Card,
  InputGroup
} from 'react-bootstrap'
import { Loader } from 'react-bootstrap-typeahead'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import TableAction from '../../components/atoms/Table/TableAction'
import TablePagination from '../../components/atoms/Table/TablePagination'
import api from '../../config/AxiosInstance'
import { convertDateWithoutTime } from '../../helpers/constants'
import toast, { Toaster } from 'react-hot-toast'

const cardFilterStyle = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  padding: '1.5rem',
  marginBottom: '1.5rem'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const customStyles = {
  control: (base, state) => ({
    ...base,
    background: '#fff',
    borderColor: state.isFocused ? '#f89d1d' : '#e9ecef',
    borderRadius: '8px',
    boxShadow: state.isFocused
      ? '0 0 0 0.2rem rgba(248, 157, 29, 0.25)'
      : 'none',
    fontSize: '0.9rem',
    padding: '2px',
    '&:hover': {
      borderColor: '#f89d1d'
    }
  }),
  placeholder: (base) => ({
    ...base,
    color: '#adb5bd'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#f89d1d'
      : state.isFocused
        ? '#fff3cd'
        : null,
    color: state.isSelected ? 'white' : '#333',
    fontSize: '0.9rem',
    cursor: 'pointer',
    ':active': {
      ...base[':active'],
      backgroundColor: '#f89d1d'
    }
  })
}

const MaterialRequest = () => {
  const initialMeta = {
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  }

  const headerTitleStyle = {
    color: '#343a40',
    fontSize: '1.5rem',
    fontWeight: '700',
    letterSpacing: '-0.5px'
  }

  const navigate = useNavigate()
  const { state } = useLocation()
  const [warehouses, setWarehouses] = useState([])
  const [targetWarehouses, setTargetWarehouses] = useState([])
  const [filter, setFilter] = useState({
    target_warehouse: state?.target_warehouse ? state.target_warehouse : ''
  })
  const [selectedTargetWarehouse, setSelectedTargetWarehouse] = useState(null)

  const [meta, setMeta] = useState(initialMeta)
  const [materialRequests, setMaterialRequests] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    fetchWarehouses()
    fetchTargetWarehouses()
  }, [])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [search])

  useEffect(() => {
    if (targetWarehouses.length > 0 && filter.target_warehouse) {
      const found = targetWarehouses.find(
        (w) => w.value === filter.target_warehouse
      )
      if (found) setSelectedTargetWarehouse(found)
    }
  }, [warehouses, targetWarehouses, filter.target_warehouse])

  useEffect(() => {
    fetchMaterialRequest()
  }, [meta.per_page, meta.current_page, filter, debouncedSearch])

  const fetchWarehouses = async () => {
    try {
      const response = await api.get(
        '/master/warehouses/all?is_parent=true&forUser=1'
      )
      const { data } = response
      if (data.success) {
        const options = data.data.map((w) => ({
          value: w.warehouse_id,
          label: w.warehouse_name
        }))
        setWarehouses(options)
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    }
  }

  const fetchTargetWarehouses = async () => {
    try {
      const response = await api.get('/master/warehouses/all?is_parent=true')
      const { data } = response
      if (data.success) {
        const options = data.data.map((w) => ({
          value: w.warehouse_id,
          label: w.warehouse_name
        }))
        setTargetWarehouses(options)
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    }
  }

  const fetchMaterialRequest = async () => {
    try {
      setIsLoading(true)
      const params = {
        target_warehouse: filter.target_warehouse,
        per_page: meta.per_page,
        page: meta.current_page
      }
      if (debouncedSearch) params.q = debouncedSearch

      const response = await api.get('/material-request/list', { params })
      const { data } = response

      if (data.success) {
        setMeta(data.metadata)
        setMaterialRequests(data.data)
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      current_page: data.selected + 1
    }))
    window.scrollTo(0, 0)
  }

  const handleFilterChange = (key, value) => {
    setFilter((prev) => ({ ...prev, [key]: value }))
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!', {
      position: 'top-right',
      style: {
        fontSize: '14px',
        fontWeight: '600'
      }
    })
  }

  const resetFilter = () => {
    setFilter({ target_warehouse: '' })
    setSelectedTargetWarehouse(null)
    setSearch('')
    setDebouncedSearch('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const FormControlStyle = {
    height: '45px',
    borderRadius: '10px',
    border: '1px solid #e9ecef',
    backgroundColor: '#ffffff',
    fontSize: '0.9rem'
  }

  return (
    <>
      <Container fluid className="px-4 mb-5">
        <Row className="mb-4 align-items-center">
          <Col>
            <h1 style={headerTitleStyle}>Material Request</h1>
          </Col>
          <Col className="text-end">
            <Button
              variant="light"
              className="text-secondary fw-bold rounded px-3 border"
              onClick={resetFilter}
              style={{ fontSize: '0.85rem' }}
            >
              <RotateCcw size={16} className="me-2" />
              Reset Filters
            </Button>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="gy-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label style={labelStyle}>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRight: 'none',
                      borderTopLeftRadius: '10px',
                      borderBottomLeftRadius: '10px'
                    }}
                  >
                    <Search size={18} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value)
                    }}
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0
                    }}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={labelStyle}>Target Warehouse</Form.Label>
                <Select
                  options={targetWarehouses}
                  value={selectedTargetWarehouse}
                  onChange={(opt) => {
                    setSelectedTargetWarehouse(opt)
                    handleFilterChange('target_warehouse', opt ? opt.value : '')
                  }}
                  placeholder="All Target Warehouses"
                  styles={customStyles}
                  isClearable
                />
              </Form.Group>
            </Col>
          </Row>
        </Card>

        <Row>
          <Col md={12}>
            <Table
              hover
              responsive
              className="shadow-sm bg-white"
              style={{
                fontSize: '0.9rem',
                borderRadius: '12px',
                overflow: 'hidden'
              }}
            >
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    #MR
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Target Warehouse
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Delivery Date
                  </th>
                  <th
                    className="p-2 align-middle text-end"
                    style={tableHeaderStyle}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={5} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!isLoading && materialRequests.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center p-5 text-muted fw-bold"
                    >
                      No Data Available
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  materialRequests.length > 0 &&
                  materialRequests.map((item) => (
                    <tr
                      key={item.id}
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <td className="p-2 align-middle fw-semibold text-primary">
                        <div className="d-flex align-items-center gap-2">
                          {item.code ?? '-'}
                          <Copy
                            size={14}
                            className="text-muted cursor-pointer"
                            style={{
                              cursor: 'pointer',
                              opacity: 0.5,
                              transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.opacity = 1)
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.opacity = 0.5)
                            }
                            onClick={() => handleCopy(item.code)}
                            title="Copy MR Code"
                          />
                        </div>
                      </td>
                      <td className="p-2 align-middle text-dark">
                        {item.target_name ?? '-'}
                      </td>
                      <td className="p-2 align-middle text-secondary">
                        {item.delivery_date
                          ? convertDateWithoutTime(item?.delivery_date)
                          : '-'}
                      </td>
                      <td className="p-2 align-middle text-end">
                        <TableAction>
                          <button
                            className="btn btn-light text-secondary border rounded"
                            title="Detail"
                            style={{
                              width: '32px',
                              height: '32px',
                              padding: 0,
                              marginRight: '8px'
                            }}
                            onClick={() => {
                              const sourceWarehouse = warehouses.find(
                                (w) => w.label === item.src_name
                              )
                              navigate(
                                `/material-request/detail/${encodeURIComponent(
                                  item.code
                                )}`,
                                {
                                  state: {
                                    target_warehouse: filter.target_warehouse,
                                    src_warehouse: filter.src_warehouse,
                                    item: {
                                      ...item,
                                      src_warehouse_id: sourceWarehouse
                                        ? sourceWarehouse.value
                                        : null
                                    }
                                  }
                                }
                              )
                            }}
                          >
                            <Search size={16} />
                          </button>
                          <button
                            className="btn btn-primary-blue btn-sm rounded px-3"
                            onClick={() => {
                              const sourceWarehouse = warehouses.find(
                                (w) => w.label === item.src_name
                              )
                              navigate(
                                `/material-request/create/${encodeURIComponent(
                                  item.code
                                )}`,
                                {
                                  state: {
                                    target_warehouse: filter.target_warehouse,
                                    src_warehouse: filter.src_warehouse,
                                    item: {
                                      ...item,
                                      src_warehouse_id: sourceWarehouse
                                        ? sourceWarehouse.value
                                        : null
                                    }
                                  }
                                }
                              )
                            }}
                          >
                            +TR
                          </button>
                        </TableAction>
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}>
                    <TablePagination
                      meta={meta}
                      handleOnChangeMeta={handleOnChangeMeta}
                      handlePageChange={handlePageChange}
                    />
                  </td>
                </tr>
              </tfoot>
            </Table>
          </Col>
        </Row>
      </Container>
      <Toaster />
    </>
  )
}

export default MaterialRequest
